import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar/Sidebar';
import TickerTape from '../components/TradingviewWidget/TickerTape';
import ChatContainer from '../components/ChatContainer';
import MessageInput from '../components/MessageInput';
import messageService from '../components/Services/messageService';

const FinAITerminal = () => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [refreshSidebar, setRefreshSidebar] = useState(0);
    const { user, logout } = useAuth();
    const sidebarRef = useRef(null);

    // Détecter si on est sur mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Charger la conversation actuelle au démarrage
    useEffect(() => {
        const loadCurrentConversation = async () => {
            const savedConversationId = messageService.getCurrentConversationId();
            if (savedConversationId) {
                try {
                    const conversation = await messageService.getConversation(savedConversationId);
                    setCurrentConversationId(savedConversationId);

                    // Convertir les messages API en format d'affichage
                    const formattedMessages = conversation.messages.map(msg => ({
                        id: `${msg.role}-${msg.timestamp}`,
                        content: msg.content,
                        isUser: msg.role === 'user',
                        timestamp: new Date(msg.timestamp)
                    }));

                    setMessages(formattedMessages);
                } catch (error) {
                    console.error('Erreur lors du chargement de la conversation:', error);
                    // Si erreur, démarrer une nouvelle conversation
                    handleNewChat();
                }
            }
        };

        if (user) {
            loadCurrentConversation();
        }
    }, [user]);

    const handleSendMessage = async (messageContent) => {
        // Ajouter le message utilisateur immédiatement à l'interface
        const userMessage = {
            id: `user-${Date.now()}`,
            content: messageContent,
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);

        try {
            // Envoyer à l'API
            const aiResponse = await messageService.processMessage(messageContent);

            // Mettre à jour l'ID de conversation si c'est un nouveau chat
            const newConversationId = messageService.getCurrentConversationId();
            if (newConversationId !== currentConversationId) {
                setCurrentConversationId(newConversationId);
                // Déclencher la mise à jour de la sidebar pour afficher la nouvelle conversation
                setRefreshSidebar(prev => prev + 1);
            }

            // Ajouter la réponse de l'IA
            const aiMessage = {
                id: `assistant-${Date.now()}`,
                content: aiResponse,
                isUser: false,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, aiMessage]);
        } catch (error) {
            console.error('Erreur lors du traitement du message:', error);

            // Afficher un message d'erreur à l'utilisateur
            const errorMessage = {
                id: `error-${Date.now()}`,
                content: `Désolé, une erreur s'est produite : ${error.message}. Veuillez réessayer.`,
                isUser: false,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSuggestionClick = (suggestionMessage) => {
        handleSendMessage(suggestionMessage);
    };

    const handleNewChat = () => {
        // Démarrer une nouvelle conversation
        messageService.startNewConversation();
        setCurrentConversationId(null);

        // Animation de disparition des messages
        const chatContainer = document.querySelector('.overflow-y-auto');
        if (chatContainer) {
            chatContainer.style.opacity = '0';
            chatContainer.style.transform = 'translateY(10px)';

            setTimeout(() => {
                setMessages([]);
                chatContainer.style.opacity = '1';
                chatContainer.style.transform = 'translateY(0)';
            }, 200);
        } else {
            setMessages([]);
        }
    };

    const handleLoadConversation = async (conversationId) => {
        try {
            setIsLoading(true);

            // Charger la conversation depuis l'API
            const conversation = await messageService.getConversation(conversationId);

            // Changer la conversation active
            messageService.loadConversation(conversationId);
            setCurrentConversationId(conversationId);

            // Convertir les messages API en format d'affichage
            const formattedMessages = conversation.messages.map(msg => ({
                id: `${msg.role}-${msg.timestamp}`,
                content: msg.content,
                isUser: msg.role === 'user',
                timestamp: new Date(msg.timestamp)
            }));

            // Réinitialiser les messages pour déclencher un chargement initial
            setMessages([]);

            // Utiliser setTimeout pour permettre au DOM de se mettre à jour
            setTimeout(() => {
                setMessages(formattedMessages);
            }, 50);

        } catch (error) {
            console.error('Erreur lors du chargement de la conversation:', error);
            // Optionnel : afficher une notification d'erreur
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="flex h-screen bg-dark-bg text-white">
            {/* Sidebar avec gestion des conversations */}
            <Sidebar
                ref={sidebarRef}
                user={user}
                onLogout={logout}
                onNewChat={handleNewChat}
                onLoadConversation={handleLoadConversation}
                currentConversationId={currentConversationId}
                refreshTrigger={refreshSidebar}
            />

            {/* Zone principale */}
            <div className={`flex-1 flex flex-col transition-all duration-200 ${isMobile ? '' : ''}`}>
                {/* Ticker Tape */}
                <TickerTape />

                {/* Zone de chat */}
                <ChatContainer
                    messages={messages}
                    onSuggestionClick={handleSuggestionClick}
                />

                {/* Zone de saisie */}
                <MessageInput
                    onSendMessage={handleSendMessage}
                    disabled={isLoading}
                />
            </div>
        </div>
    );
};

export default FinAITerminal;
